import React, { CSSProperties, useEffect, useMemo, useState } from "react";

import { Button, Divider, Form, Input, Modal } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import DateTextBox8 from "react-lib/apps/common/DateTextBox8";

import {
  ACTIONS,
  BTN_ACTS,
  HolidayInfoType,
  PickedState,
  RunSequence,
  SetProp,
} from "./sequence/HolidaySetting";

import { adToBeWithSetFormat } from "react-lib/utils/dateUtils";

type ModHolidayInfoProps = Pick<PickedState, "buttonLoadCheck"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  data: Partial<HolidayInfoType> | null;
  holidays: Date[];
  open: boolean;
  year: number;
  onClose: () => void;
};

type Styles = Record<
  "buttonsWrapper" | "closeButton" | "deleteButton" | "header" | "modal" | "saveButton",
  CSSProperties
>;

const styles: Styles = {
  buttonsWrapper: {
    display: "flex",
    gap: "20px",
    marginTop: "4rem",
  },
  closeButton: {
    background: "#A7A7A7",
    borderRadius: "5px",
    color: "#FFFFFF",
    padding: "0.85rem 1rem",
    width: "10.5rem",
  },
  deleteButton: {
    background: "#A7A7A7",
    borderRadius: "5px",
    color: "#FFFFFF",
    padding: "0.85rem 1rem",
    width: "10.5rem",
  },
  modal: {
    borderRadius: "16px",
    overflow: "hidden",
    width: "50rem",
  },
  saveButton: {
    background: "var(--primary-theme-color)",
    borderRadius: "5px",
    color: "#FFFFFF",
    padding: "0.85rem 1rem",
    width: "10.5rem",
  },
  header: {
    fontSize: "1.25rem",
    fontWeight: "bold",
    marginBottom: "10px",
  },
};

const ModHolidayInfo = (props: ModHolidayInfoProps) => {
  const [dates, setDates] = useState<string[]>([]);
  const [detail, setDetail] = useState("");
  const [remark, setRemark] = useState("");

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const dateList = props.data?.dates ?? [];

    if (dateList.length > 0) {
      setDates(dateList.map((date) => adToBeWithSetFormat(date, "YYYY-MM-DD", "DD-MM-YYYY")));
    } else {
      setDates([]);
    }

    setDetail(props.data?.detail || "");
    setRemark(props.data?.remark || "");
  }, [props.data, props.open]);

  const handleSave = () => {
    const data = { dates, detail, remark };

    props.runSequence({
      sequence: "HolidaySetting",
      action: ACTIONS.SAVE,
      data,
      onSuccess: props.onClose,
    });
  };

  const handleDelete = () => {
    const { data, onClose } = props;

    if (data) {
      props.runSequence({
        sequence: "HolidaySetting",
        action: ACTIONS.DELETE,
        data: {
          ...data,
          dates: (data.dates ?? []).map((date) =>
            adToBeWithSetFormat(date, "YYYY-MM-DD", "DD-MM-YYYY")
          ),
          detail: data.detail || "",
          remark: data.remark || "",
        },
        onSuccess: onClose,
      });
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  const valueList = useMemo(() => dates.map((item: string) => item.replaceAll("/", "-")), [dates]);

  return (
    <Modal style={styles.modal} open={props.open} onClose={handleClose}>
      <Modal.Content style={{ padding: "1.25rem 1.5rem 2.5rem" }}>
        <div style={styles.header}>
          {props.data?.dates ? "รายละเอียดข้อมูลวันหยุด" : "เพิ่มรายการวันหยุดใหม่"}
        </div>

        <Divider style={{ borderTop: "1px solid var(--primary-theme-color)" }} />

        <Form style={{ marginTop: "2rem" }}>
          <Form.Group style={{ marginBottom: "2rem" }} inline>
            <Form.Field width={4} required>
              <label style={{ fontSize: "1.1rem" }}>วันที่</label>
            </Form.Field>
            <Form.Field width={6}>
              <div style={{ width: "100%" }}>
                <DateTextBox8
                  defaultYear={props.year}
                  mode="multiple"
                  modifiers={{ customHoliday: props.holidays }}
                  modifiersStyles={{ customHoliday: { color: "red" } }}
                  valueList={valueList}
                  onChange={({ string }: any) => {
                    const dateArray: string[] = string ? (string as string).split(",") : [];

                    setDates(dateArray);
                  }}
                />
              </div>
            </Form.Field>
          </Form.Group>

          <Form.Group style={{ marginBottom: "2rem" }} inline>
            <Form.Field width={4} required>
              <label style={{ fontSize: "1.1rem" }}>ชื่อวันหยุด</label>
            </Form.Field>
            <Form.Field style={{ paddingRight: 0 }} width={12}>
              <Input
                placeholder="ระบุระรายละเอียดวันหยุด เช่น ชื่อวันหยุด"
                value={detail}
                fluid
                onChange={(e) => {
                  setDetail(e.target.value);
                }}
                input={{
                  style: {
                    border: "1px solid #D9D9D9",
                    borderRadius: "6px",
                  },
                }}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group style={{ marginBottom: "2rem" }}>
            <Form.Field width={4}>
              <label style={{ fontSize: "1.1rem" }}>หมายเหตุ</label>
            </Form.Field>
            <Form.Field width={12}>
              <Input
                placeholder="ระบุระรายละเอียดวันหยุด เช่น ชื่อวันหยุด"
                value={remark}
                fluid
                onChange={(e) => {
                  setRemark(e.target.value);
                }}
                input={{
                  style: {
                    border: "1px solid #D9D9D9",
                    borderRadius: "6px",
                  },
                }}
              />
            </Form.Field>
          </Form.Group>
        </Form>

        <div style={styles.buttonsWrapper}>
          {props.data?.dates && (
            <ButtonLoadCheck
              setProp={props.setProp}
              style={styles.deleteButton}
              paramKey={BTN_ACTS.DELETE}
              size="medium"
              title="ลบวันหยุด"
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.DELETE]}
              onClick={handleDelete}
            />
          )}

          <div style={{ flex: 1 }} />

          <Button style={styles.closeButton} onClick={handleClose}>
            ปิดหน้าจอ
          </Button>

          <ButtonLoadCheck
            setProp={props.setProp}
            style={styles.saveButton}
            disabled={dates.length === 0 || !detail}
            paramKey={BTN_ACTS.SAVE}
            size="medium"
            title="บันทึกข้อมูล"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
            onClick={handleSave}
          />
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default React.memo(ModHolidayInfo);
